import React, { useState } from 'react';
import { Comments } from './comments/Comments';
import { RichTextEditor } from './editor/RichTextEditor';
import { TaskAttachment } from '../../../types/TaskAttachment';
import { CommentAttachment } from '../../../types/CommentAttachment';
import { BaseAttachments } from './BaseAttachments';

export interface TaskCommentsProps {
  comments: any[];
  isLoading: boolean;
  onCommentSubmit: (commentValue: string, tempAttachmentKeys?: string[], parentId?: number) => Promise<void>;
  onEditComment?: (commentId: number, content: string) => void;
  onDeleteComment?: (commentId: number) => void;
  usersWithAccessToSpace: any[];
  onImageUpload: (file: File) => Promise<TaskAttachment | null>;
  onCommentAttachmentUpload: (files: File[]) => Promise<CommentAttachment[]>;
  onCopyTaskDescriptionLink: () => void;
  showWarningMessage: (message: string) => void;
  hideHeading?: boolean;
}

export const TaskComments: React.FC<TaskCommentsProps> = React.memo(
  ({
    comments,
    isLoading,
    onCommentSubmit,
    onEditComment,
    onDeleteComment,
    usersWithAccessToSpace,
    onImageUpload,
    onCommentAttachmentUpload,
    onCopyTaskDescriptionLink,
    showWarningMessage,
    hideHeading = false,
  }) => {
    const [commentValue, setCommentValue] = useState('');
    const [tempAttachments, setTempAttachments] = useState<CommentAttachment[]>([]);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);

    const handleCommentAttachmentUpload = async (files: File[]): Promise<CommentAttachment[]> => {
      setIsUploadingFiles(true);
      try {
        const attachments = await onCommentAttachmentUpload(files);
        if (attachments && attachments.length > 0) {
          setTempAttachments((prev) => [...prev, ...attachments]);
          return attachments;
        }
        return [];
      } finally {
        setIsUploadingFiles(false);
      }
    };

    const handleCommentAttachmentDelete = (attachment: CommentAttachment) => {
      setTempAttachments(tempAttachments.filter((a) => a.key !== attachment.key));
    };

    const handleOnCommentSubmit = async () => {
      // Prevent submission while files are uploading
      if (isUploadingFiles) {
        showWarningMessage('Please wait for the files to upload before submitting the comment.');
        return;
      }

      try {
        await onCommentSubmit(
          commentValue,
          tempAttachments.map((a) => a.key)
        );
        // Only clear the composer once the comment is confirmed posted — clearing
        // unconditionally would lose the user's text on a failed submission.
        setCommentValue('');
        setTempAttachments([]);
      } catch {
        // Error is already surfaced by onCommentSubmit; keep the text so the user can retry.
      }
    };

    return (
      <>
        {!hideHeading && <div className="text-brand-grey-dark text-xs font-semibold">Comments</div>}
        {isLoading || comments?.length ? (
          <Comments
            loading={isLoading}
            comments={comments}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
            onReplySubmit={(content, parentId) => onCommentSubmit(content, undefined, parentId)}
            usersWithAccessToSpace={usersWithAccessToSpace}
          />
        ) : (
          <div className="flex flex-col justify-center items-center"></div>
        )}
        <div className="flex flex-row w-full pt-2 gap-2">
          <div className="flex-grow">
            <RichTextEditor
              value={commentValue}
              onChange={setCommentValue}
              onSubmit={handleOnCommentSubmit}
              usersWithAccessToSpace={usersWithAccessToSpace}
              showSubmitButton={true}
              submitButtonText="Comment"
              placeholder="Write a comment..."
              minHeight={'90px'}
              mode="comment"
              onImageUpload={onImageUpload}
              onCopyTaskDescriptionLink={onCopyTaskDescriptionLink}
              onCommentAttachmentUpload={handleCommentAttachmentUpload}
              middleContent={
                <div className="px-4 pt-1">
                  <BaseAttachments
                    attachments={tempAttachments}
                    onDeleteAttachment={handleCommentAttachmentDelete}
                    isDraftMode={true}
                  />
                </div>
              }
            />
          </div>
        </div>
      </>
    );
  }
);
